import { create } from 'zustand'
import { Device, Connection, Zone, Layout } from '@/types/devices'
import { normalizeDevicePorts } from '@/lib/presetToDevice'

export type Mutation =
  | { type: 'addDevice'; device: Device }
  | { type: 'updateDevice'; device: Device }
  | { type: 'deleteDevice'; deviceId: string }
  | {
      type: 'updateDevicePosition'
      deviceId: string
      position: { x: number; y: number }
    }
  | { type: 'addConnection'; connection: Connection }
  | { type: 'removeConnection'; connectionId: string }
  | { type: 'createZone'; zone: Zone }
  | { type: 'updateZone'; zone: Zone }
  | { type: 'deleteZone'; zoneId: string }

/**
 * When a layout room is active (multiplayer), every mutation
 * should route through Liveblocks Storage — the shared CRDT —
 * rather than directly updating this Zustand store. The sync
 * hook (`useMultiplayerLayoutSync`) installs a dispatcher on
 * mount that writes to Storage; it then also mirrors Storage
 * changes back into Zustand via `applyRemoteSnapshot`, so peers
 * see each other's edits in real time while UI components keep
 * reading from `useLayoutStore` as before.
 *
 * When no dispatcher is installed — offline / `/editor/local-dev`
 * / users without the teamSharing feature — every action falls
 * through to the legacy path: direct state update plus an entry
 * appended to `mutationQueue` for `useAutoSave` to batch-save to
 * DynamoDB.
 */
export interface LayoutDispatcher {
  addDevice(device: Device): void
  updateDevice(device: Device): void
  deleteDevice(deviceId: string): void
  updateDevicePosition(
    deviceId: string,
    position: { x: number; y: number },
  ): void
  addConnection(connection: Connection): void
  removeConnection(connectionId: string): void
  createZone(zone: Zone): void
  updateZone(zone: Zone): void
  deleteZone(zoneId: string): void
}

let remoteDispatcher: LayoutDispatcher | null = null

/**
 * Install the remote dispatcher. Pass `null` to uninstall (room
 * unmount). Mutations that arrive while the dispatcher is set
 * write to Storage instead of Zustand — the Storage subscription
 * then updates Zustand via `applyRemoteSnapshot`.
 */
export function installRemoteDispatcher(
  dispatcher: LayoutDispatcher | null,
): void {
  remoteDispatcher = dispatcher
}

/**
 * Snapshot applied to Zustand by the Storage subscription when
 * remote peers change something. Intentionally does *not* append
 * to `mutationQueue` — the Storage path persists through
 * `useMultiplayerAutoSave` (leader-elected DDB save), not the
 * per-mutation batch writer.
 */
export interface RemoteSnapshot {
  devices: Device[]
  connections: Connection[]
  zones: Zone[]
  name?: string
  version?: number
}

interface LayoutStoreState {
  layoutId: string | null
  layoutName: string
  devices: Device[]
  connections: Connection[]
  zones: Zone[]
  layoutVersion: number
  mutationQueue: Mutation[]
  isSaving: boolean

  addDevice: (device: Device) => void
  updateDevice: (device: Device) => void
  deleteDevice: (deviceId: string) => void
  updateDevicePosition: (
    deviceId: string,
    position: { x: number; y: number },
  ) => void
  addConnection: (connection: Connection) => void
  removeConnection: (connectionId: string) => void
  createZone: (zone: Zone) => void
  updateZone: (zone: Zone) => void
  deleteZone: (zoneId: string) => void
  flushMutationQueue: () => Mutation[]
  hydrate: (layout: Layout) => void
  setIsSaving: (saving: boolean) => void
  setLayoutVersion: (version: number) => void
  /**
   * Replace the devices / connections / zones arrays + optional
   * meta fields with a remote-sourced snapshot. Used by the
   * Liveblocks Storage subscription; bypasses `mutationQueue`.
   */
  applyRemoteSnapshot: (snapshot: RemoteSnapshot) => void
}

export const useLayoutStore = create<LayoutStoreState>((set, get) => ({
  layoutId: null,
  layoutName: '',
  devices: [],
  connections: [],
  zones: [],
  layoutVersion: 0,
  mutationQueue: [],
  isSaving: false,

  addDevice: device => {
    if (remoteDispatcher) {
      remoteDispatcher.addDevice(device)
      return
    }
    // A device with no zoneId is "unassigned" — it sits at the layout
    // level and renders as an individual device in the overview.
    set(state => ({
      devices: [...state.devices, device],
      zones: device.zoneId
        ? state.zones.map(z =>
            z.id === device.zoneId && !z.deviceIds.includes(device.id)
              ? { ...z, deviceIds: [...z.deviceIds, device.id] }
              : z,
          )
        : state.zones,
      mutationQueue: [...state.mutationQueue, { type: 'addDevice', device }],
    }))
  },

  updateDevice: device => {
    if (remoteDispatcher) {
      remoteDispatcher.updateDevice(device)
      return
    }
    set(state => {
      const prev = state.devices.find(d => d.id === device.id)
      const zoneChanged = prev && prev.zoneId !== device.zoneId
      return {
        devices: state.devices.map(d => (d.id === device.id ? device : d)),
        // Keep zone.deviceIds consistent with device.zoneId. When a
        // device moves between zones, remove it from its previous
        // zone's list and add it to the new one.
        zones: zoneChanged
          ? state.zones.map(z => {
              if (z.id === prev.zoneId) {
                return {
                  ...z,
                  deviceIds: z.deviceIds.filter(id => id !== device.id),
                }
              }
              if (z.id === device.zoneId && !z.deviceIds.includes(device.id)) {
                return { ...z, deviceIds: [...z.deviceIds, device.id] }
              }
              return z
            })
          : state.zones,
        mutationQueue: [
          ...state.mutationQueue,
          { type: 'updateDevice', device },
        ],
      }
    })
  },

  deleteDevice: deviceId => {
    if (remoteDispatcher) {
      remoteDispatcher.deleteDevice(deviceId)
      return
    }
    set(state => ({
      devices: state.devices.filter(d => d.id !== deviceId),
      connections: state.connections.filter(
        c => c.sourceDeviceId !== deviceId && c.targetDeviceId !== deviceId,
      ),
      mutationQueue: [
        ...state.mutationQueue,
        { type: 'deleteDevice', deviceId },
      ],
    }))
  },

  updateDevicePosition: (deviceId, position) => {
    if (remoteDispatcher) {
      remoteDispatcher.updateDevicePosition(deviceId, position)
      return
    }
    set(state => ({
      devices: state.devices.map(d =>
        d.id === deviceId ? { ...d, position } : d,
      ),
      mutationQueue: [
        ...state.mutationQueue,
        { type: 'updateDevicePosition', deviceId, position },
      ],
    }))
  },

  addConnection: connection => {
    if (remoteDispatcher) {
      remoteDispatcher.addConnection(connection)
      return
    }
    set(state => ({
      connections: [...state.connections, connection],
      mutationQueue: [
        ...state.mutationQueue,
        { type: 'addConnection', connection },
      ],
    }))
  },

  removeConnection: connectionId => {
    if (remoteDispatcher) {
      remoteDispatcher.removeConnection(connectionId)
      return
    }
    set(state => ({
      connections: state.connections.filter(c => c.id !== connectionId),
      mutationQueue: [
        ...state.mutationQueue,
        { type: 'removeConnection', connectionId },
      ],
    }))
  },

  createZone: zone => {
    if (remoteDispatcher) {
      remoteDispatcher.createZone(zone)
      return
    }
    set(state => ({
      zones: [...state.zones, zone],
      mutationQueue: [...state.mutationQueue, { type: 'createZone', zone }],
    }))
  },

  updateZone: zone => {
    if (remoteDispatcher) {
      remoteDispatcher.updateZone(zone)
      return
    }
    set(state => ({
      zones: state.zones.map(z => (z.id === zone.id ? zone : z)),
      mutationQueue: [...state.mutationQueue, { type: 'updateZone', zone }],
    }))
  },

  deleteZone: zoneId => {
    if (remoteDispatcher) {
      remoteDispatcher.deleteZone(zoneId)
      return
    }
    // Deleting a zone sets its devices back to the unassigned state
    // (zoneId: undefined) so they pop back onto the layout level
    // rather than disappearing entirely.
    set(state => ({
      zones: state.zones.filter(z => z.id !== zoneId),
      devices: state.devices.map(d =>
        d.zoneId === zoneId ? { ...d, zoneId: undefined } : d,
      ),
      mutationQueue: [...state.mutationQueue, { type: 'deleteZone', zoneId }],
    }))
  },

  applyRemoteSnapshot: snapshot => {
    set(state => ({
      devices: snapshot.devices,
      connections: snapshot.connections,
      zones: snapshot.zones,
      layoutName:
        snapshot.name !== undefined ? snapshot.name : state.layoutName,
      layoutVersion:
        snapshot.version !== undefined ? snapshot.version : state.layoutVersion,
    }))
  },

  flushMutationQueue: () => {
    const state = get()
    const queue = state.mutationQueue
    set({ mutationQueue: [] })
    return queue
  },

  hydrate: layout => {
    // Migrate older snapshots: drop any legacy Default zone (Phase 1a
    // stored one at id === 'default') and convert device references to
    // it into unassigned (zoneId: undefined). Also clear any device
    // whose zoneId points to a zone that isn't in the snapshot anymore.
    const realZones = layout.zones.filter(z => z.id !== 'default')
    const zoneIds = new Set(realZones.map(z => z.id))
    const migratedDevices = layout.devices
      .map(d =>
        d.zoneId && zoneIds.has(d.zoneId) ? d : { ...d, zoneId: undefined },
      )
      // Backfill bidirectional twin ports on devices that were
      // persisted before USB ports were auto-expanded — ensures USB
      // connectors on legacy devices show up on both sides of the
      // canvas without requiring a manual DB migration.
      .map(normalizeDevicePorts)
    set({
      layoutId: layout.id,
      layoutName: layout.name,
      devices: migratedDevices,
      connections: layout.connections,
      zones: realZones,
      layoutVersion: layout.version,
      mutationQueue: [],
    })
  },

  setIsSaving: saving => set({ isSaving: saving }),
  setLayoutVersion: version => set({ layoutVersion: version }),
}))
